// The Responses tab: what was captured through the share link, newest first.
//
// Each response is the steps of the VERSION it was given against, compiled
// through the same runtime the visitor filled them in through — `step-form`
// in read-only mode, which renders each widget's View rather than its Edit,
// so a date reads as a date and a list as a table rather than as the JSON
// underneath. Every published version is kept whole, so the answers are read
// against exactly the declaration that asked for them however often the
// workflow has been published since; the versions are read once for the tab
// (queries/workflow-version.fetchAll) and each card looks its own up by the
// `version` on the row. A response from before versions existed has none,
// and is read against the current draft, as it always was.
//
// Two things can be done from here. Every response can be DOWNLOADED as one
// JSON file (./export), and any one of them can be DELETED — in place, after a
// second click, because a response is somebody's answers and the app's other
// removals each sit on a screen of their own; a card is too small for that.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Badge, Card, CardBody, CardHeader } from "reactstrap";
import { useApolloClient, useMutation } from "@apollo/client";
import * as Notify from "@repoflow/form-schema/notify";
import { Icon as LoadingIcon } from "@repoflow/components/loading";
import { List } from "@repoflow/components/list-cursor";
import CapturePublicSchema from "@common/schema/capture/public";
import { missingSubforms } from "@common/schema/types";
import { Form as StepForm } from "@shared/step-form";
import * as widgets from "@shared/widgets/register";
import { StepTabs } from "components/step-tabs";
import * as CaptureQL from "queries/capture";
import * as WorkflowVersionQL from "queries/workflow-version";
import * as Export from "./export";

const when = (iso) => {
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? "" : date.toLocaleString();
};

// Every field of a step, the hidden ones included. The visitor never saw a
// hidden field, but the page that embedded the form filled it in — which
// site, which campaign (see the fill app's components/flow/prefill) — and
// that is exactly what the tenant reads responses to tell apart.
const reveal = (step) => ({
  ...step,
  params: (step.params || []).map((param) =>
    param.hidden ? { ...param, hidden: false } : param
  ),
});

const StepAnswer = ({ step, subforms, values }) => {
  if (!values) {
    return <p className="text-muted mb-0">No answer recorded for this step.</p>;
  }

  const missing = missingSubforms(step, subforms);
  if (missing.length > 0) {
    return (
      <p className="text-danger mb-0">
        This step asks for a subform that is not declared:{" "}
        <code>{missing.join(", ")}</code>.
      </p>
    );
  }

  return (
    <StepForm
      step={step}
      subforms={subforms}
      widgets={widgets}
      values={values}
      readOnly
    />
  );
};

// Delete, asked twice. The first click turns the control into the question
// and its two answers; the second is the one that sends. Not window.confirm:
// a native dialog blocks the page and cannot be styled, addressed or driven.
const Delete = ({ onRemove, removing }) => {
  const [asked, setAsked] = useState(false);

  if (removing) {
    return (
      <span className="text-muted small">
        <LoadingIcon /> Deleting…
      </span>
    );
  }

  if (!asked) {
    return (
      <button
        type="button"
        className="btn btn-link btn-sm text-danger p-0 response-delete"
        onClick={() => setAsked(true)}
      >
        Delete
      </button>
    );
  }

  return (
    <span className="response-delete-ask">
      <span className="text-danger small mr-2">Delete this response?</span>
      <button
        type="button"
        className="btn btn-danger btn-sm response-delete-confirm"
        onClick={onRemove}
      >
        Delete
      </button>{" "}
      <button
        type="button"
        className="btn btn-secondary btn-sm response-delete-cancel"
        onClick={() => setAsked(false)}
      >
        Cancel
      </button>
    </span>
  );
};

const Response = ({ item: capture, definitionOf, onRemove }) => {
  // Open by default — a response is what somebody came to this tab to read —
  // and collapsible per card so a long list can be skimmed by time.
  const [open, setOpen] = useState(true);
  const [removing, setRemoving] = useState(false);

  // The declaration the answers were given against, every field shown.
  const { steps: declared, subforms, version } = definitionOf(capture);
  const steps = useMemo(() => declared.map(reveal), [declared]);

  const remove = async () => {
    setRemoving(true);
    // Only back to the button on failure: on success the card is gone.
    if (!(await onRemove(capture))) setRemoving(false);
  };

  return (
    <Card className="workflow-response mb-3">
      <CardHeader
        className="d-flex justify-content-between align-items-center"
        style={{ cursor: "pointer" }}
        onClick={() => setOpen(!open)}
      >
        <span>
          <i className={`fa fa-caret-${open ? "down" : "right"} mr-2`} />
          {when(capture.createdAt)}
        </span>
        <span
          className="d-flex align-items-center"
          // The header toggles the card; the controls in it must not.
          onClick={(e) => e.stopPropagation()}
        >
          <small className="text-muted mr-3">
            <code>{capture.captureId}</code>
          </small>
          <Badge
            color={version ? "secondary" : "light"}
            pill
            className="mr-3 response-version"
            title={version ? "The version the visitor was shown" : "Given before versions existed; read against the current draft"}
          >
            {version ? `version ${version}` : "no version"}
          </Badge>
          <Delete onRemove={remove} removing={removing} />
        </span>
      </CardHeader>
      {open ? (
        <CardBody>
          {/* One step per tab, as the visitor saw them, so a long workflow's
              answers are read a step at a time rather than as one scroll. */}
          <StepTabs steps={steps}>
            {(step, index) => (
              <StepAnswer
                key={step.name || index}
                step={step}
                subforms={subforms}
                values={(capture.values || {})[step.name]}
              />
            )}
          </StepTabs>
        </CardBody>
      ) : null}
    </Card>
  );
};

// One empty list, not a new one per render: the memo below keys on identity.
const NONE = [];

// The whole list as a file. Walks every page through the client and hands the
// browser the document; the button is dark while that runs, since a workflow
// with many pages takes a moment and a second click would start over.
const Download = ({ workflow, versions }) => {
  const client = useApolloClient();
  const [busy, setBusy] = useState(false);

  const run = async () => {
    setBusy(true);
    try {
      const captures = await Export.fetchAll(client, workflow.workflowId);
      const doc = Export.document(workflow, captures, { versions: [...versions.values()] });
      Export.download(Export.filename(workflow), JSON.stringify(doc, null, 2));
    } catch (error) {
      console.error("capture.export:", error);
      Notify.error("The responses could not be downloaded. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <button
      type="button"
      id="responses-download"
      className="btn btn-outline-primary btn-sm"
      disabled={busy}
      onClick={run}
    >
      {busy ? <LoadingIcon /> : <i className="fa fa-download mr-1" />} Download JSON
    </button>
  );
};

// Every version the workflow has, keyed by number, read once when the tab
// mounts and again when the workflow's published number moves. Null until
// read, so the cards can wait rather than render every answer against the
// draft and then jump.
const useVersions = (workflowId, publishedVersion) => {
  const client = useApolloClient();
  const [versions, setVersions] = useState(null);

  useEffect(() => {
    let live = true;
    if (!publishedVersion) {
      setVersions(new Map());
      return undefined;
    }
    WorkflowVersionQL.fetchAll(client, workflowId)
      .then((items) => {
        if (live) setVersions(new Map(items.map((v) => [v.version, v])));
      })
      .catch((error) => {
        console.error("workflow.versions:", error);
        if (live) setVersions(new Map());
      });
    return () => {
      live = false;
    };
  }, [client, workflowId, publishedVersion]);

  return versions;
};

export const ResponsesTab = ({ workflow }) => {
  const { workflowId, steps = NONE, subforms = NONE } = workflow;
  const publishedVersion = workflow.publishedVersion || 0;
  const versions = useVersions(workflowId, publishedVersion);

  // Which declaration a response is read against: its version's, or the
  // draft's when the row has no version or names one this tab could not read.
  const definitionOf = useCallback(
    (capture) => {
      const version = versions && capture.version ? versions.get(capture.version) : null;
      return version
        ? { steps: version.steps || NONE, subforms: version.subforms || NONE, version: version.version }
        : { steps, subforms, version: capture.version || 0 };
    },
    [versions, steps, subforms]
  );

  // What was deleted from this tab since it mounted. The mutation evicts the
  // capture from the cache, which takes it out of the list's query; this is
  // the same fact kept here, so the card goes the moment the API answers
  // rather than when the list next reconciles a page it may have accumulated.
  const [removed, setRemoved] = useState(() => new Set());
  const filter = useCallback((item) => !removed.has(item.captureId), [removed]);

  const [remove] = useMutation(CaptureQL.remove);

  // Answers whether the row is gone, so the card knows whether to come back.
  const onRemove = useCallback(
    async (capture) => {
      try {
        await remove({
          variables: { workflowId, captureId: capture.captureId },
          update: (cache) => {
            cache.evict({ id: cache.identify(capture) });
            cache.gc();
          },
        });
        setRemoved((previous) => new Set(previous).add(capture.captureId));
        Notify.success("Response deleted.");
        return true;
      } catch (error) {
        console.error("capture.remove:", error);
        Notify.error("The response could not be deleted. Please try again.");
        return false;
      }
    },
    [remove, workflowId]
  );

  // Memoised on the declaration: a new component type every render would
  // remount every card and lose which ones were collapsed.
  const Component = useMemo(
    () => (props) => (
      <Response {...props} definitionOf={definitionOf} onRemove={onRemove} />
    ),
    [definitionOf, onRemove]
  );

  if (versions === null) {
    return (
      <div id="workflow-responses" className="mt-3 text-muted">
        <LoadingIcon /> Reading the workflow&apos;s versions…
      </div>
    );
  }

  return (
    <div id="workflow-responses" className="mt-3">
      <div className="d-flex justify-content-end mb-3">
        <Download workflow={workflow} versions={versions} />
      </div>
      <List
        mode="list"
        path={"viewer.account.workflows.get.captures"}
        query={CaptureQL.list}
        variables={{ workflowId }}
        schema={CapturePublicSchema}
        widgets={widgets}
        orderBy="createdAt"
        // Always fresh: a response given a moment ago is what this tab is
        // opened to see, and the cache cannot know it arrived.
        fetchPolicy="cache-and-network"
        filter={filter}
        empty="No responses yet. Share the workflow and they will appear here as they come in."
        component={Component}
      />
    </div>
  );
};
